import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpErrorResponse,
  HttpEvent,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { AuthStore } from './store/auth.store';
import { AuthApiService } from '../services/auth-api.service';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authStore = inject(AuthStore);
  const authApiService = inject(AuthApiService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (
        error.status === 401 &&
        !req.url.includes('/auth/refresh') &&
        !req.url.includes('/auth/login')
      ) {
        return handle401Error(req, next, authStore, authApiService);
      }
      return throwError(() => error);
    })
  );
};

function handle401Error(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authStore: InstanceType<typeof AuthStore>,
  authApiService: AuthApiService
): Observable<HttpEvent<unknown>> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const refreshToken = localStorage.getItem('refresh_token');

    if (!refreshToken) {
      isRefreshing = false;
      logoutAndRedirect(authStore);
      return throwError(() => new Error('Session expired'));
    }

    return authApiService.refreshToken({ refresh_token: refreshToken }).pipe(
      tap((response) => {
        const tokens = response.data;
        isRefreshing = false;
        localStorage.setItem('access_token', tokens.access_token);
        localStorage.setItem('refresh_token', tokens.refresh_token);
        localStorage.setItem('expires_in', tokens.expires_in.toString());
        if (tokens.session_id) {
          localStorage.setItem('session_id', tokens.session_id);
        }
        authStore.loadFromStorage();
        refreshTokenSubject.next(tokens.access_token);
      }),
      switchMap((response) =>
        next(cloneWithToken(req, response.data.access_token))
      ),
      catchError((err) => {
        isRefreshing = false;
        logoutAndRedirect(authStore);
        return throwError(() => err);
      })
    );
  }

  return refreshTokenSubject.pipe(
    filter((token) => token !== null),
    take(1),
    switchMap((token) => next(cloneWithToken(req, token as string)))
  );
}

function cloneWithToken(
  req: HttpRequest<unknown>,
  token: string
): HttpRequest<unknown> {
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

function logoutAndRedirect(authStore: InstanceType<typeof AuthStore>): void {
  authStore.logout().then(() => {
    window.location.href = '/auth/login';
  });
}
